// Word cloud — sized by how many topics + AI-suggested search terms contain
// each word, colored by the dominant readiness score among those topics.
//
// Much simpler than the Lens cloud: no POS extraction, no compromise.js. The
// trend-tracker corpus is curated topic names + search terms, which are
// already nouns/noun-phrases by construction. Stopwords filter the obvious
// connector words; everything else surfaces.

import type { Topic, TopicWithContext } from "../lib/types.ts";

export interface CloudCallbacks {
  onWordClick: (term: string, contextTopicIds: number[]) => void;
}

const STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "of", "to", "in", "for", "with", "on", "by",
  "based", "using", "via", "from", "into", "across", "between", "as", "at",
  "is", "are", "be", "been", "system", "systems", "model", "models",
]);

export function renderCloud(
  host: HTMLElement,
  topicsCtx: TopicWithContext[],
  cb: CloudCallbacks,
): () => void {
  host.innerHTML = "";

  // Tally tokens. For each topic, contribute its name + search_terms.
  // For each token, track which topics carry it (so click → drill-down).
  const counts = new Map<string, { count: number; topicIds: Set<number>; readiness: number[] }>();
  for (const tc of topicsCtx) {
    const name = tc.topic.name.toLowerCase();
    const tokens = name.split(/[^a-z0-9'-]+/).filter((t) => t.length >= 4 && !STOPWORDS.has(t));
    const searchTokens = (tc.topic.search_terms || []).flatMap((s) =>
      s.toLowerCase().split(/[^a-z0-9'-]+/).filter((t) => t.length >= 4 && !STOPWORDS.has(t)),
    );
    const seen = new Set([...tokens, ...searchTokens]);
    for (const t of seen) {
      let rec = counts.get(t);
      if (!rec) {
        rec = { count: 0, topicIds: new Set(), readiness: [] };
        counts.set(t, rec);
      }
      rec.count += 1;
      rec.topicIds.add(tc.topic.id);
      const r = tc.readiness?.avg_readiness_v1 ?? null;
      if (r !== null) rec.readiness.push(r);
    }
  }

  // Top 80 by frequency, with a noise floor that scales with corpus size.
  const minCount = topicsCtx.length <= 6 ? 1 : Math.max(2, Math.floor(topicsCtx.length * 0.05));
  const top = Array.from(counts.entries())
    .filter(([, v]) => v.count >= minCount)
    .sort(([, a], [, b]) => b.count - a.count)
    .slice(0, 80);

  if (!top.length) {
    host.innerHTML = `<div class="viz-empty">Not enough topics to derive a cloud yet.</div>`;
    return () => { host.innerHTML = ""; };
  }

  const maxCount = top[0][1].count;
  const minCountVal = top[top.length - 1][1].count;
  const sizeFor = (c: number) =>
    maxCount === minCountVal
      ? 22
      : Math.round(14 + ((c - minCountVal) / (maxCount - minCountVal)) * 36);

  // Color by mean readiness: cool (low) → warm (high). Three steps suffice.
  const colorFor = (readiness: number[]): string => {
    if (!readiness.length) return "#b7bdc8";
    const mean = readiness.reduce((a, b) => a + b, 0) / readiness.length;
    if (mean >= 2.3) return "#7ec25f";  // hot
    if (mean >= 1.7) return "#f7b955";  // warming
    return "#6ba8ff";                    // cool
  };

  const container = document.createElement("div");
  container.className = "cloud-words";
  host.appendChild(container);

  for (const [word, rec] of top) {
    const span = document.createElement("span");
    span.className = "cloud-word";
    span.textContent = word;
    span.style.fontSize = `${sizeFor(rec.count)}px`;
    span.style.color = colorFor(rec.readiness);
    span.style.fontWeight = sizeFor(rec.count) >= 28 ? "700" : "500";
    span.style.opacity = String(0.65 + (sizeFor(rec.count) - 14) / 36 * 0.35);
    span.style.cursor = "pointer";
    span.title = `"${word}" · ${rec.count} topic${rec.count === 1 ? "" : "s"}`;
    span.addEventListener("click", () => cb.onWordClick(word, Array.from(rec.topicIds)));
    container.appendChild(span);
  }

  return () => { host.innerHTML = ""; };
}

export function _topicNameTokens(topic: Topic): string[] {
  // Exposed for testing / future reuse — same tokenization the cloud uses.
  return topic.name.toLowerCase()
    .split(/[^a-z0-9'-]+/)
    .filter((t) => t.length >= 4 && !STOPWORDS.has(t));
}
